const length = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
const merge = (spans) => {
    const out = [];
    for (const [lo, hi] of spans.filter(([lo, hi]) => hi - lo > 1e-8).sort((a, b) => a[0] - b[0])) {
        const last = out.at(-1);
        if (last && lo <= last[1] + 1e-8)
            last[1] = Math.max(last[1], hi);
        else
            out.push([lo, hi]);
    }
    return out;
};
const subtract = (spans, gaps) => spans.flatMap(([start, end]) => {
    let at = start;
    const out = [];
    for (const [lo, hi] of merge(gaps)) {
        if (hi <= at || lo >= end)
            continue;
        if (lo > at)
            out.push([at, Math.min(lo, end)]);
        at = Math.max(at, hi);
    }
    if (at < end)
        out.push([at, end]);
    return out;
});
/** Structural wall ink uses the same doorway placements as the edge finish. */
export function physicalWallRuns(floor) {
    const scale = Number(floor.scaleMmPerPx) || 0;
    return (floor.walls ?? []).flatMap((wall, index) => {
        if (!wall.a || !wall.b)
            return [];
        const a = wall.a, b = wall.b, L = length(a, b);
        if (L < 1e-8)
            return [];
        const gaps = [];
        for (const d of floor.wallDoors ?? []) {
            const resolved = (d.wallID ? (floor.walls ?? []).findIndex(w => w.id?.toLowerCase() === d.wallID.toLowerCase()) : -1);
            if ((resolved >= 0 ? resolved : d.wallIndex) !== index || scale <= 0)
                continue;
            const at = d.posMM ?? 0, half = Math.max(0, d.widthMM ?? 0) / scale / 2;
            gaps.push([at - half, at + half]);
        }
        return subtract([[0, L]], gaps).map(([lo, hi]) => ({ a: { x: a.x + (b.x - a.x) * lo / L, y: a.y + (b.y - a.y) * lo / L }, b: { x: a.x + (b.x - a.x) * hi / L, y: a.y + (b.y - a.y) * hi / L }, thickness: Math.max(1, (wall.thicknessMM ?? floor.wallThicknessMM ?? 0) / (scale || 1)) }));
    });
}
/** Physical wall faces, cut at jambs and every non-wall opening, in native canvas points.
 * posMM is a legacy name: door position is in canvas points; widthMM is millimetres.
 * A room outline can close an area across empty space; it does not establish a wall.
 */
export function wallEdgeRuns(floor, area, doorScaleForEdge) {
    const poly = area.boundary ?? [];
    if (poly.length < 3)
        return [];
    const scale = Number(area.scaleMmPerPx ?? floor.scaleMmPerPx) || 0, tol = scale > 0 ? 150 / scale : 2;
    const walls = (floor.walls ?? []).filter((w) => !!w.a && !!w.b && length(w.a, w.b) > 1e-8);
    const sign = poly.reduce((n, p, i) => { const q = poly[(i + 1) % poly.length]; return n + p.x * q.y - q.x * p.y; }, 0) >= 0 ? 1 : -1;
    const result = [];
    for (let edge = 0; edge < poly.length; edge++) {
        const p = poly[edge], q = poly[(edge + 1) % poly.length], L = length(p, q);
        if (L < 1e-8)
            continue;
        const ux = (q.x - p.x) / L, uy = (q.y - p.y) / L;
        const along = (v) => (v.x - p.x) * ux + (v.y - p.y) * uy;
        const off = (v) => (v.x - p.x) * -uy + (v.y - p.y) * ux;
        const overlap = (a, b) => [Math.max(0, Math.min(along(a), along(b))), Math.min(L, Math.max(along(a), along(b)))];
        const candidates = walls.filter(w => Math.abs(off(w.a) - off(w.b)) < 1e-4 && Math.abs(off(w.a)) <= tol + ((w.thicknessMM ?? floor.wallThicknessMM ?? 0) / (scale || 1)) / 2 && overlap(w.a, w.b)[1] - overlap(w.a, w.b)[0] > 1e-8)
            .sort((a, b) => Math.abs(off(a.a)) - Math.abs(off(b.a)));
        let assigned = [];
        for (const w of walls.length ? candidates : [null]) {
            const base = w ? overlap(w.a, w.b) : [0, L];
            let spans = subtract([base], assigned);
            assigned = merge([...assigned, base]);
            const gaps = [];
            for (const e of floor.openEdges ?? []) {
                if (!e.a || !e.b)
                    continue;
                if (Math.abs(off(e.a)) <= tol && Math.abs(off(e.b)) <= tol)
                    gaps.push(overlap(e.a, e.b));
            }
            if (scale > 0) {
                const addDoor = (d, a, b) => {
                    const len = length(a, b);
                    if (len < 1e-8)
                        return;
                    const dx = (b.x - a.x) / len, dy = (b.y - a.y) / len;
                    if (Math.abs(dx * uy - dy * ux) > 1e-4)
                        return;
                    const c = { x: a.x + dx * (d.posMM ?? 0), y: a.y + dy * (d.posMM ?? 0) };
                    if (Math.abs(off(c)) > tol)
                        return;
                    const half = Math.max(0, d.widthMM ?? 0) / (doorScaleForEdge?.(edge) ?? scale) / 2, t = along(c);
                    gaps.push([Math.max(0, t - half), Math.min(L, t + half)]);
                };
                for (const d of area.doorways ?? []) {
                    const i = ((d.wallIndex ?? 0) % poly.length + poly.length) % poly.length;
                    if (i === edge)
                        addDoor(d, poly[i], poly[(i + 1) % poly.length]);
                }
                for (const d of floor.wallDoors ?? []) {
                    const wall = (d.wallID ? (floor.walls ?? []).find(v => v.id?.toLowerCase() === d.wallID.toLowerCase()) : null) ?? (floor.walls ?? [])[d.wallIndex ?? -1];
                    if (!wall?.a || !wall.b)
                        continue;
                    // The room may be offset from its wall; a different nearby parallel wall is not its opening.
                    if (w && (Math.abs(off(wall.a) - off(w.a)) > 1e-4 || Math.abs(off(wall.b) - off(w.b)) > 1e-4))
                        continue;
                    addDoor(d, wall.a, wall.b);
                }
            }
            spans = subtract(spans, gaps);
            const offset = w ? off(w.a) + sign * (w.thicknessMM ?? floor.wallThicknessMM ?? 0) / (scale || 1) / 2 : 0;
            for (const [lo, hi] of spans)
                result.push({ a: { x: p.x + ux * lo - uy * offset, y: p.y + uy * lo + ux * offset }, b: { x: p.x + ux * hi - uy * offset, y: p.y + uy * hi + ux * offset }, edge, from: lo / L, to: hi / L });
        }
    }
    return result;
}
//# sourceMappingURL=wall-edge-runs.js.map